import { useCallback, useState } from 'react';
import { doc, updateDoc } from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import { db } from '../../../providers/firebase/firebase';
import type { GateDoc } from '../../../types/gates';

interface UseGateDecisionResult {
    submit: (
        gate: GateDoc,
        action: 'approve' | 'deny' | 'close',
        amountUsd?: number | null
    ) => Promise<void>;
    submitting: boolean;
}

/**
 * Writes the user's decision onto a gate doc. Dot-path update so only the
 * `decision` key is affected (the rules reject anything else), RFC3339
 * string timestamp because bogg parses decided_at with parse_from_rfc3339 —
 * NOT serverTimestamp().
 *
 * amountUsd semantics for approve (design/GATE.md §3):
 * - a number   → the granted cap
 * - null       → explicit UNBOUNDED (no cap)
 * - undefined  → default to the algo's proposed_limit_usd
 * Deny and close always write approved_limit_usd null. deny = freeze at
 * current size (never sold); close = sell to flat and block re-entry until
 * the decision changes (design/GATE.md §1/§4, design/GATE_UI.md §1).
 *
 * If bogg bumped request_generation since this gate was rendered, the rules
 * reject the write (acted_on_generation must equal the doc's current
 * request_generation) — callers surface that and the onSnapshot listener
 * delivers the fresh proposal.
 */
export function useGateDecision(): UseGateDecisionResult {
    const [submitting, setSubmitting] = useState(false);

    const submit = useCallback(
        async (
            gate: GateDoc,
            action: 'approve' | 'deny' | 'close',
            amountUsd?: number | null
        ) => {
            const uid = getAuth().currentUser?.uid;
            if (!uid) throw new Error('not_signed_in');
            setSubmitting(true);
            try {
                await updateDoc(
                    doc(
                        db,
                        `gates/${gate.account_id}/markets/${gate.condition_id}`
                    ),
                    {
                        'decision.action': action,
                        'decision.approved_limit_usd':
                            action === 'approve'
                                ? amountUsd === undefined
                                    ? gate.proposed_limit_usd
                                    : amountUsd
                                : null,
                        'decision.acted_on_generation': gate.request_generation,
                        'decision.decided_at': new Date().toISOString(),
                        'decision.decided_by': uid,
                    }
                );
            } finally {
                setSubmitting(false);
            }
        },
        []
    );

    return { submit, submitting };
}
